import { admitFlowPassenger } from './arrivals.js';
import { advanceFlowPorters } from './baggage.js';
import { advanceFlowService } from './service.js';
import { openFlowBoarding, advanceFlowFlights } from './flights.js';
import { randomInteger } from './random.js';
import type { FlowFacts, FlowPolicy, FlowResult, FlowState } from './types.js';

/** An airport's L1 state with empty pools; cash and counters are deltas from the moment the model takes over. */
export function initialFlowState(facts: FlowFacts): FlowState {
    return {
        frame: facts.sourceFrame, randomState: facts.initialRandomState, serial: facts.serial, spawnClock: facts.spawnClock,
        nextArrivalIn: facts.nextArrivalIn, dissatisfied: facts.dissatisfied, moneyLimit: facts.moneyLimit,
        departments: facts.departments.map(d => ({ index: d.index, queued: [], inService: [], completed: [], pending: 0, cashRemainder: 0, servedMilli: 0, litter: 0 })), litter: 0,
        vending: facts.vending.map(v => ({ index: v.index, pending: 0, cashRemainder: 0, soldMilli: 0 })),
        lounge: [], inquiry: [], transit: [], reservations: [], inbound: [], planes: structuredClone(facts.planes),
        porters: Array.from({ length: facts.porters }, () => ({ phase: 'rack' as const, remainingFrames: 0, carriedMilli: 0 })),
        freeBagMilli: 0, producedBagMilli: 0, deliveredBagMilli: 0,
        settledRevenue: 0, businessRevenue: 0, blockedRevenue: 0, flights: 0, admittedMilli: 0, boardedMilli: 0, walkedOutMilli: 0, landedMilli: 0, exitedMilli: 0,
    };
}

/** Passenger, bag and posted-cash conservation; any failure means the state cannot be trusted. */
export function flowConservationError(state: FlowState): string | null {
    const people = [...state.lounge, ...state.inquiry, ...state.transit, ...state.reservations].reduce((sum, p) => sum + p.massMilli, 0)
        + state.departments.reduce((sum, d) => sum + [...d.queued, ...d.inService, ...d.completed].reduce((mass, p) => mass + p.massMilli, 0), 0);
    if (state.admittedMilli !== people + state.boardedMilli + state.walkedOutMilli) return 'Outgoing passenger mass not conserved.';
    if (state.landedMilli !== state.inbound.reduce((sum, p) => sum + p.massMilli, 0) + state.exitedMilli) return 'Inbound passenger mass not conserved.';
    if (state.producedBagMilli !== state.freeBagMilli + state.porters.reduce((sum, p) => sum + p.carriedMilli, 0) + state.deliveredBagMilli) return 'Bag mass not conserved.';
    const cash = state.departments.reduce((sum, d) => sum + d.pending, 0) + state.vending.reduce((sum, v) => sum + v.pending, 0);
    if (cash + state.settledRevenue !== state.businessRevenue) return 'Posted business cash not conserved.';
    return null;
}

/**
 * One deterministic second of L1. Returns errors as values and never mutates its input; `half` posts half business
 * cash with carried remainders, and `frozen` neither ages nor earns (Act I unoccupied airports).
 */
export function stepFlow(source: FlowState, facts: FlowFacts, policy: FlowPolicy = 'active'): FlowResult {
    if (!['active', 'half', 'frozen'].includes(policy)) return { ok: false, error: 'Invalid flow policy.' };
    const error = validateFlowState(source, facts) ?? flowConservationError(source);
    if (error) return { ok: false, error };
    const state = structuredClone(source);
    if (policy === 'frozen') return { ok: true, state };
    advanceOneSecond(state, facts, policy === 'half');
    const after = validateFlowState(state, facts) ?? flowConservationError(state);
    return after ? { ok: false, error: after } : { ok: true, state };
}

/**
 * Many seconds at once: validates once, clones once and steps in place, then re-checks conservation.
 * Proven equal to repeated stepFlow; this is the catch-up path, so it must not cost a clone per second.
 */
export function runFlow(source: FlowState, facts: FlowFacts, seconds: number, policy: FlowPolicy = 'active'): FlowResult {
    if (!['active', 'half', 'frozen'].includes(policy)) return { ok: false, error: 'Invalid flow policy.' };
    if (!Number.isSafeInteger(seconds) || seconds < 0) return { ok: false, error: 'Flow time must be whole nonnegative seconds.' };
    const error = validateFlowState(source, facts) ?? flowConservationError(source);
    if (error) return { ok: false, error };
    const state = structuredClone(source);
    if (policy === 'frozen') return { ok: true, state };
    for (let n = 0; n < seconds; n++) advanceOneSecond(state, facts, policy === 'half');
    const after = validateFlowState(state, facts) ?? flowConservationError(state);
    return after ? { ok: false, error: after } : { ok: true, state };
}

function advanceOneSecond(state: FlowState, facts: FlowFacts, half: boolean): void {
    state.frame += 60;
    advanceFlowTravel(state, facts);
    admitFlowPassenger(state, facts);
    openFlowBoarding(state, facts);
    advanceFlowService(state, facts, half);
    advanceFlowPorters(state, facts);
    advanceFlowFlights(state, facts);
}

function advanceFlowTravel(state: FlowState, facts: FlowFacts): void {
    for (const passenger of [...state.transit]) {
        passenger.remainingFrames -= 60;
        if (passenger.remainingFrames > 0) continue;
        state.transit.splice(state.transit.indexOf(passenger), 1);
        const { destination, remainingFrames: _, ...arrived } = passenger;
        if (destination === 6) { state.lounge.push(arrived); continue; }
        const department = state.departments.find(d => d.index === destination)!;
        // A snack buyer's wrapper misses the bin by one fair draw on joining Security; staff never clean it.
        if (destination === 2 && passenger.serial % 6 === 0 && (!facts.litterBin || randomInteger(state, 0, 1) === 1) && state.litter < facts.litterCap) {
            state.litter++;
            for (const index of facts.litterNear[Math.min(11, department.queued.length)][passenger.serial % 12]) state.departments.find(d => d.index === index)!.litter++;
        }
        department.queued.push(arrived);
    }
    for (const passenger of [...state.reservations]) {
        passenger.remainingFrames -= 60;
        if (passenger.remainingFrames > 0) continue;
        const plane = state.planes.find(p => p.gate === passenger.gate)!;
        plane.boarded++; state.boardedMilli += passenger.massMilli;
        state.reservations.splice(state.reservations.indexOf(passenger), 1);
    }
}

/** Move all posted desk and machine cash out to the floor, exactly once; the returned amounts are what the airport now holds. */
export function settleFlowCash(source: FlowState): { state: FlowState; departments: { index: number; amount: number }[]; vending: { index: number; amount: number }[] } {
    const state = structuredClone(source);
    const departments = state.departments.map(d => ({ index: d.index, amount: d.pending }));
    const vending = state.vending.map(v => ({ index: v.index, amount: v.pending }));
    for (const account of [...state.departments, ...state.vending]) { state.settledRevenue += account.pending; account.pending = 0; }
    return { state, departments, vending };
}

export function validateFlowState(state: FlowState, facts: FlowFacts): string | null {
    if (!state || !facts || !Array.isArray(state.departments) || !Array.isArray(facts.departments) || !state.departments.length || state.departments.length !== facts.departments.length)
        return 'Invalid flow department shape.';
    for (const name of ['lounge', 'inquiry', 'transit', 'reservations', 'inbound', 'planes', 'porters', 'vending'] as const)
        if (!Array.isArray(state[name])) return 'Invalid flow pool shape.';
    for (const [n, d] of state.departments.entries())
        if (!d || d.index !== facts.departments[n].index || !Array.isArray(d.queued) || !Array.isArray(d.inService) || !Array.isArray(d.completed)) return 'Invalid flow department pools.';
    if (state.planes.some(p => !p.visiting)) return 'L1 supports visiting airlines only.';
    const numbers = [state.frame, state.serial, state.spawnClock, state.nextArrivalIn, state.dissatisfied, state.moneyLimit, state.litter,
        state.freeBagMilli, state.producedBagMilli, state.deliveredBagMilli, state.settledRevenue, state.businessRevenue, state.blockedRevenue,
        state.flights, state.admittedMilli, state.boardedMilli, state.walkedOutMilli, state.landedMilli, state.exitedMilli,
        ...state.departments.flatMap(d => [d.pending, d.cashRemainder, d.servedMilli, d.litter]), ...state.vending.flatMap(v => [v.pending, v.cashRemainder, v.soldMilli])];
    if (numbers.some(n => !Number.isSafeInteger(n) || n < 0)) return 'Flow counters must be nonnegative safe integers.';
    if ([...state.lounge, ...state.inquiry, ...state.transit, ...state.reservations, ...state.inbound, ...state.departments.flatMap(d => [...d.queued, ...d.inService, ...d.completed])]
        .some(p => !p || p.massMilli !== 1000)) return 'Discrete cohorts contain exactly 1000 milli-passengers.';
    // Every nested value the step reads must be finite, so stored or received state can never poison a step.
    const finite = (...values: unknown[]) => values.every(v => typeof v === 'number' && Number.isFinite(v));
    const travellers = [...state.lounge, ...state.inquiry, ...state.transit, ...state.departments.flatMap(d => [...d.queued, ...d.inService, ...d.completed])];
    if (travellers.some(p => !Number.isSafeInteger(p.serial) || typeof p.vip !== 'boolean' || !finite(p.patienceFrames, p.leavingFrames)
        || [p.happiness, p.remainingWork, p.approachFrames, p.slot].some(v => v !== undefined && !finite(v)))) return 'Flow travellers must carry finite fields.';
    if (state.transit.some(p => !Number.isSafeInteger(p.destination) || !finite(p.remainingFrames)) || state.inquiry.some(p => !finite(p.walkFrames, p.answerFrames))
        || [...state.reservations, ...state.inbound].some(p => !Number.isSafeInteger(p.serial) || !Number.isSafeInteger(p.gate) || !finite(p.remainingFrames)) || state.inbound.some(p => !finite(p.gateFrames)))
        return 'Flow movement records must carry finite fields.';
    if (state.porters.some(p => !p || !['rack', 'loading', 'returning'].includes(p.phase) || !finite(p.remainingFrames, p.carriedMilli))) return 'Flow porters are invalid.';
    if (state.planes.some(p => !p || typeof p.boardingCalled !== 'boolean' || !finite(p.gate, p.phase, p.clock, p.boarded, p.seats, p.flightNumber, p.tripFrames, p.nextDepartureFrame,
        p.scheduledDepartureFrame, p.boardingOpensFrame, p.scheduledArrivalFrame, p.expectedArrivalFrame, p.boardingAnnouncedFrame, p.unattendedSince, p.aircraftReadyFrame,
        p.boardingStartedFrame, p.departureReadyFrame, p.boardingDeskFrames, p.departureOffset, p.arrivalOffset))) return 'Flow planes are invalid.';
    return null;
}

/** Shape and conservation check for stored L1 state, given the airport's department chain. */
export function validateStoredFlow(state: FlowState, departmentIndices: number[]): string | null {
    if (!state || typeof state !== 'object') return 'Flow state is missing.';
    return validateFlowState(state, { departments: departmentIndices.map(index => ({ index })) } as FlowFacts) ?? flowConservationError(state);
}
